/**
 * Data layer for `brand-color-logo-uploader`. Thin typed fetch wrappers over /api/v1/brand-color-logo-uploader.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface BrandColorLogoUploaderRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListBrandColorLogoUploaderResponse {
	count: number;
	items: BrandColorLogoUploaderRow[];
}

export interface CreateBrandColorLogoUploaderBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/brand-color-logo-uploader";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListBrandColorLogoUploaderResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBrandColorLogoUploaderResponse>(res);
}

export async function create(body: CreateBrandColorLogoUploaderBody): Promise<BrandColorLogoUploaderRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<BrandColorLogoUploaderRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const brandColorLogoUploaderQueryKeys = {
	all: ["brand-color-logo-uploader"] as const,
	list: () => [...brandColorLogoUploaderQueryKeys.all] as const,
	detail: (id: string) => [...brandColorLogoUploaderQueryKeys.all, id] as const,
};
